import { useEffect, useMemo, useState } from "react";
import OwnerLayout from "@/components/OwnerLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  MessageCircle, DollarSign, XCircle, Search,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import {
  format, startOfWeek, addDays, addWeeks, subWeeks,
  startOfMonth, endOfMonth, addMonths, subMonths, isSameMonth, isSameDay, parseISO,
} from "date-fns";
import { es } from "date-fns/locale";

const STATUS_LABELS = {
  pending: "Pendiente",
  confirmed: "Confirmada",
  paid: "Pagada",
  cancelled: "Cancelada",
};

function statusStyle(s) {
  switch (s) {
    case "paid": return "text-gold border-gold/40 bg-gold/10";
    case "confirmed": return "text-cyan border-cyan/40 bg-cyan/10";
    case "cancelled": return "text-neutral-500 border-neutral-700 bg-neutral-900";
    default: return "text-neonpink border-neonpink/40 bg-neonpink/10";
  }
}

function AppointmentRow({ a, business, onAction }) {
  const sendReminder = () => {
    const bname = business?.name || "";
    const msg = encodeURIComponent(
      `Hola ${a.client_name}! Te recuerdo tu cita de ${a.service_name} el ${a.date} a las ${a.time} en ${bname}. ¡Te esperamos! ✨`
    );
    const phone = a.client_phone.replace(/\D/g, "");
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };

  return (
    <div className="p-4 md:p-5 flex flex-col lg:flex-row lg:items-center gap-3">
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className="font-mono text-cyan text-sm text-right w-24">
          <div className="text-neutral-500 text-[10px] uppercase tracking-widest">{a.date}</div>
          <div className="text-lg leading-none">{a.time}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{a.client_name}</div>
          <div className="text-xs text-neutral-500 truncate">
            {a.service_name} · ${a.price} · Anticipo ${a.deposit} · {a.client_phone}
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border shrink-0 ${statusStyle(a.status)}`}>
          {STATUS_LABELS[a.status]}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="border-cyan/40 text-cyan hover:bg-cyan/10 rounded-full"
          onClick={sendReminder} data-testid={`remind-${a.id}`}>
          <MessageCircle className="w-3.5 h-3.5 mr-1" /> Recordar
        </Button>
        <Button size="sm" className="bg-gold text-void hover:bg-gold/90 rounded-full font-bold"
          onClick={() => onAction("pay", a)}
          disabled={a.status === "paid" || a.status === "cancelled"}
          data-testid={`pay-${a.id}`}>
          <DollarSign className="w-3.5 h-3.5 mr-1" /> Pagada
        </Button>
        <Button size="sm" variant="ghost" className="text-neonpink hover:bg-neonpink/10 rounded-full"
          onClick={() => onAction("cancel", a)}
          disabled={a.status === "cancelled"}
          data-testid={`cancel-${a.id}`}>
          <XCircle className="w-3.5 h-3.5 mr-1" /> Cancelar
        </Button>
      </div>
    </div>
  );
}

function DayView({ items, business, onAction, refDate, setRefDate }) {
  const dayItems = items
    .filter((a) => a.date === format(refDate, "yyyy-MM-dd"))
    .sort((x, y) => x.time.localeCompare(y.time));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setRefDate(addDays(refDate, -1))} className="p-2 text-neutral-400 hover:text-gold" data-testid="day-prev">
          <ChevronLeft />
        </button>
        <div className="text-center">
          <p className="overline">Día</p>
          <h3 className="font-display text-2xl" data-testid="day-title">{format(refDate, "EEEE d 'de' MMMM", { locale: es })}</h3>
        </div>
        <button onClick={() => setRefDate(addDays(refDate, 1))} className="p-2 text-neutral-400 hover:text-gold" data-testid="day-next">
          <ChevronRight />
        </button>
      </div>
      <div className="bg-surface gold-border rounded-2xl overflow-hidden" data-testid="day-list">
        {dayItems.length === 0 ? (
          <div className="p-10 text-center text-neutral-500 text-sm">Sin citas ese día.</div>
        ) : (
          <div className="divide-y divide-gold/10">
            {dayItems.map((a) => <AppointmentRow key={a.id} a={a} business={business} onAction={onAction} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function WeekView({ items, refDate, setRefDate, onSelectDay }) {
  const start = startOfWeek(refDate, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));

  const byDay = (d) => items
    .filter((a) => a.date === format(d, "yyyy-MM-dd") && a.status !== "cancelled")
    .sort((x, y) => x.time.localeCompare(y.time));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setRefDate(subWeeks(refDate, 1))} className="p-2 text-neutral-400 hover:text-gold" data-testid="week-prev">
          <ChevronLeft />
        </button>
        <div className="text-center">
          <p className="overline">Semana</p>
          <h3 className="font-display text-2xl" data-testid="week-title">
            {format(start, "d MMM", { locale: es })} — {format(addDays(start, 6), "d MMM yyyy", { locale: es })}
          </h3>
        </div>
        <button onClick={() => setRefDate(addWeeks(refDate, 1))} className="p-2 text-neutral-400 hover:text-gold" data-testid="week-next">
          <ChevronRight />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="week-grid">
        {days.map((d) => {
          const list = byDay(d);
          const today = isSameDay(d, new Date());
          return (
            <button
              key={d.toISOString()}
              onClick={() => onSelectDay(d)}
              className={`text-left bg-surface rounded-2xl p-3 min-h-[130px] border transition-colors duration-200 ${
                today ? "border-cyan/50" : "border-gold/10 hover:border-gold/30"
              }`}
              data-testid={`week-day-${format(d, "yyyy-MM-dd")}`}
            >
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                {format(d, "EEE", { locale: es })}
              </div>
              <div className={`font-display text-2xl mb-2 ${today ? "text-cyan" : "text-gold"}`}>{format(d, "d")}</div>
              <div className="space-y-1">
                {list.slice(0, 3).map((a) => (
                  <div key={a.id} className="text-[11px] px-2 py-1 rounded bg-void/60 border border-gold/10 truncate">
                    <span className="text-cyan font-mono">{a.time}</span> · {a.client_name}
                  </div>
                ))}
                {list.length > 3 && (
                  <div className="text-[10px] text-neutral-500">+{list.length - 3} más</div>
                )}
                {list.length === 0 && <div className="text-[10px] text-neutral-600">—</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MonthView({ items, refDate, setRefDate, onSelectDay }) {
  const monthStart = startOfMonth(refDate);
  const monthEnd = endOfMonth(refDate);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const totalDays = 42; // 6 rows
  const days = Array.from({ length: totalDays }, (_, i) => addDays(gridStart, i));
  const daysWithCount = useMemo(() => {
    const map = new Map();
    for (const a of items) {
      if (a.status === "cancelled") continue;
      map.set(a.date, (map.get(a.date) || 0) + 1);
    }
    return map;
  }, [items]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setRefDate(subMonths(refDate, 1))} className="p-2 text-neutral-400 hover:text-gold" data-testid="month-prev">
          <ChevronLeft />
        </button>
        <div className="text-center">
          <p className="overline">Mes</p>
          <h3 className="font-display text-2xl capitalize" data-testid="month-title">
            {format(refDate, "MMMM yyyy", { locale: es })}
          </h3>
        </div>
        <button onClick={() => setRefDate(addMonths(refDate, 1))} className="p-2 text-neutral-400 hover:text-gold" data-testid="month-next">
          <ChevronRight />
        </button>
      </div>
      <div className="grid grid-cols-7 text-center overline mb-2">
        {["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"].map((d) => (
          <div key={d} className="py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2" data-testid="month-grid">
        {days.map((d) => {
          const inMonth = isSameMonth(d, refDate);
          const count = daysWithCount.get(format(d, "yyyy-MM-dd")) || 0;
          const today = isSameDay(d, new Date());
          return (
            <button
              key={d.toISOString()}
              onClick={() => onSelectDay(d)}
              className={`aspect-square rounded-xl bg-surface border relative flex flex-col items-center justify-center transition-colors duration-200 ${
                today ? "border-cyan/60" : "border-gold/10 hover:border-gold/30"
              } ${!inMonth ? "opacity-30" : ""}`}
              data-testid={`month-day-${format(d, "yyyy-MM-dd")}`}
            >
              <div className={`font-display text-lg ${today ? "text-cyan" : ""}`}>{format(d, "d")}</div>
              {count > 0 && (
                <div className="absolute bottom-2 flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-neonpink" />
                  <span className="text-[10px] font-mono text-gold">{count}</span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Appointments() {
  const [items, setItems] = useState([]);
  const [business, setBusiness] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("list"); // list | day | week | month
  const [refDate, setRefDate] = useState(new Date());

  const load = async () => {
    try {
      const [apts, biz] = await Promise.all([api.get("/appointments"), api.get("/business/me")]);
      setItems(apts.data);
      setBusiness(biz.data);
    } catch (e) {}
  };

  useEffect(() => { load(); }, []);

  const onAction = async (kind, a) => {
    if (kind === "cancel" && !window.confirm("¿Cancelar esta cita?")) return;
    try {
      await api.patch(`/appointments/${a.id}/${kind === "pay" ? "pay" : "cancel"}`);
      toast.success(kind === "pay" ? "Marcada como pagada" : "Cita cancelada");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filtered = items.filter((a) => {
    if (filter !== "all" && a.status !== filter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return a.client_name.toLowerCase().includes(s) || a.client_phone.includes(s) || a.service_name.toLowerCase().includes(s);
  });

  const selectDay = (d) => { setRefDate(d); setView("day"); };

  const views = [
    { k: "list", l: "Lista" },
    { k: "day", l: "Día" },
    { k: "week", l: "Semana" },
    { k: "month", l: "Mes" },
  ];

  return (
    <OwnerLayout>
      <div className="mb-8">
        <p className="overline mb-2">Todas las citas</p>
        <h1 className="font-display text-4xl md:text-5xl" data-testid="appointments-title">Agenda</h1>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {views.map((v) => (
          <button
            key={v.k}
            onClick={() => setView(v.k)}
            data-testid={`view-${v.k}`}
            className={`px-5 h-11 rounded-full text-xs uppercase tracking-widest transition-colors duration-200 ${
              view === v.k ? "bg-gold text-void font-bold" : "bg-surface text-neutral-400 hover:text-gold gold-border"
            }`}
          >
            {v.l}
          </button>
        ))}
      </div>

      {view === "list" && (
        <>
          <div className="flex flex-col md:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <Input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por nombre, teléfono o servicio"
                data-testid="appointments-search"
                className="pl-9 bg-surface border-gold/20 h-12"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto hide-scrollbar">
              {[
                { k: "all", l: "Todas" },
                { k: "pending", l: "Pendientes" },
                { k: "confirmed", l: "Confirmadas" },
                { k: "paid", l: "Pagadas" },
                { k: "cancelled", l: "Canceladas" },
              ].map((t) => (
                <button
                  key={t.k}
                  onClick={() => setFilter(t.k)}
                  data-testid={`filter-${t.k}`}
                  className={`px-4 h-12 rounded-full whitespace-nowrap text-xs uppercase tracking-widest transition-colors duration-200 ${
                    filter === t.k ? "bg-gold text-void font-bold" : "bg-surface text-neutral-400 hover:text-gold gold-border"
                  }`}
                >
                  {t.l}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-surface gold-border rounded-2xl overflow-hidden" data-testid="appointments-list">
            {filtered.length === 0 ? (
              <div className="p-10 text-center text-neutral-500 text-sm">Sin citas para mostrar.</div>
            ) : (
              <div className="divide-y divide-gold/10">
                {filtered.map((a) => <AppointmentRow key={a.id} a={a} business={business} onAction={onAction} />)}
              </div>
            )}
          </div>
        </>
      )}

      {view === "day" && <DayView items={items} business={business} onAction={onAction} refDate={refDate} setRefDate={setRefDate} />}
      {view === "week" && <WeekView items={items} refDate={refDate} setRefDate={setRefDate} onSelectDay={selectDay} />}
      {view === "month" && <MonthView items={items} refDate={refDate} setRefDate={setRefDate} onSelectDay={selectDay} />}
    </OwnerLayout>
  );
}
